/* ========== SPARKLINE PARSER ==========
 * Scans a worksheet's XML for <x14:sparklineGroup> elements and emits one
 * chart object per <x14:sparkline> inside them. Each sparkline becomes a
 * tiny single-cell chart with chartType "sparkline-line",
 * "sparkline-column", or "sparkline-winLoss".
 *
 * Each group also carries its AUTHORED style, which we parse and pass to
 * the renderer:
 *   - colors:  x14:colorSeries / colorNegative / colorMarkers /
 *              colorHigh / colorLow / colorFirst / colorLast
 *              (rgb="FFRRGGBB" or theme="N" + tint)
 *   - flags:   markers / high / low / first / last / negative attributes
 *              ("1" = that point-highlight is enabled)
 *
 * Sparklines live inside the worksheet XML, not the drawing relationships,
 * which is why they need their own extractor.
 * ====================================== */

import { qLocal, qLocalAll } from "../xmlUtils";
import { parseCellRef } from "./cellRefs";
import { readRangeValues } from "./workbookContext";
import { getThemeColors } from "./colorResolver";

const TYPE_TO_SUBTYPE = {
  line:    "sparkline-line",
  column:  "sparkline-column",
  stacked: "sparkline-winLoss",
};

/** Excel tint: >0 lightens toward white, <0 darkens toward black. */
function applyTint(hex, tint) {
  if (!tint) return "#" + hex;
  const num = parseInt(hex, 16);
  let r = (num >> 16) & 0xff, g = (num >> 8) & 0xff, b = num & 0xff;
  const t = Math.max(-1, Math.min(1, tint));
  const adj = (c) => Math.round(t > 0 ? c * (1 - t) + 255 * t : c * (1 + t));
  r = adj(r); g = adj(g); b = adj(b);
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

/**
 * Resolve one of the group's color elements (x14:colorSeries,
 * x14:colorNegative, x14:colorHigh, …) to a hex string.
 */
function readGroupColor(group, localName) {
  const node = qLocal(group, localName);
  if (!node) return null;
  const rgb = node.getAttribute("rgb");
  if (rgb && rgb.length >= 6) return "#" + rgb.slice(-6).toUpperCase();
  const themeAttr = node.getAttribute("theme");
  if (themeAttr != null) {
    const themeColors = getThemeColors();
    const idx = parseInt(themeAttr, 10);
    const base = themeColors && themeColors[idx];
    if (base) {
      const tint = parseFloat(node.getAttribute("tint") || "0");
      return applyTint(base, isNaN(tint) ? 0 : tint);
    }
  }
  return null;
}

/** Read all authored colors + point-highlight flags off a sparklineGroup. */
function readGroupStyle(group) {
  const flag = (name) => {
    const v = group.getAttribute(name);
    return v === "1" || v === "true";
  };
  return {
    colors: {
      series:   readGroupColor(group, "colorSeries"),
      negative: readGroupColor(group, "colorNegative"),
      markers:  readGroupColor(group, "colorMarkers"),
      high:     readGroupColor(group, "colorHigh"),
      low:      readGroupColor(group, "colorLow"),
      first:    readGroupColor(group, "colorFirst"),
      last:     readGroupColor(group, "colorLast"),
    },
    show: {
      markers:  flag("markers"),
      high:     flag("high"),
      low:      flag("low"),
      first:    flag("first"),
      last:     flag("last"),
      negative: flag("negative"),
    },
  };
}

/**
 * Extract every sparkline defined on a sheet. Returns chart-like objects
 * with isSparkline=true and a one-cell range tied to <x14:sqref>.
 */
export async function extractSparklinesForSheet(zip, ctx, sheetPath, sheetName) {
  const out = [];
  const sheetFile = zip.file(sheetPath);
  if (!sheetFile) return out;

  const xml = await sheetFile.async("string");
  if (!xml || xml.indexOf("sparklineGroup") === -1) return out;

  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const groups = qLocalAll(doc, "sparklineGroup");
  if (!groups.length) return out;

  let idx = 0;
  for (const group of groups) {
    const type = (group.getAttribute("type") || "line").toLowerCase();
    const subType = TYPE_TO_SUBTYPE[type] || "sparkline-line";
    const style = readGroupStyle(group);

    const sparklineNodes = qLocalAll(group, "sparkline");
    for (const sl of sparklineNodes) {
      const fNode = qLocal(sl, "f");
      const sqrefNode = qLocal(sl, "sqref");
      if (!fNode || !sqrefNode) continue;

      const cellRef = (sqrefNode.textContent || "").trim();
      const pos = parseCellRef(cellRef);
      if (!pos) continue;

      // Read data values (sparkline ranges are sheet-qualified or local)
      let formula = (fNode.textContent || "").trim();
      if (formula.indexOf("!") === -1) formula = sheetName + "!" + formula;
      const cellVals = await readRangeValues(zip, ctx, formula);
      const data = cellVals.map(v => {
        const n = parseFloat(v);
        return isNaN(n) ? 0 : n;
      });
      if (!data.length) continue;

      out.push({
        id:           "sparkline-" + sheetName + "-" + (idx++),
        type:         "chart",
        chartType:    subType,
        isSparkline:  true,
        title:        "",
        series:       [{ name: "Sparkline", data, renderType: subType }],
        categories:   [],
        sparkColors:  style.colors,
        sparkShow:    style.show,
        range: {
          tl: { col: pos.col,     row: pos.row,     colOff: 0, rowOff: 0 },
          br: { col: pos.col + 1, row: pos.row + 1, colOff: 0, rowOff: 0 },
        },
      });
    }
  }
  return out;
}
